import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, query, where, updateDoc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth, ROLE_ADMIN, ROLE_PROJECT_RADAR, ROLE_COLLABORATOR_VIEWER, ROLE_COLLABORATOR_ADMIN, ROLE_COLLABORATOR_EDITOR, ROLE_SALARY_ADMIN, ROLE_SALARY_VIEW, ROLE_CALCULATOR, ROLE_EVALUATION_ADMIN, ROLE_VACATION_DP, ROLE_VACATION_VIEWER, ROLE_SI_ADMIN, ROLE_SI_RH, ROLE_PUBLICADOR, ROLE_RECRUITMENT_ADMIN } from '../../contexts/AuthContext';
import { UserCircleIcon, PencilIcon, TrashIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

interface User {
    id: string;
    email: string;
    displayName?: string;
    photoURL?: string;
    roles: string[];
}

const roleDisplayNames: { [key: string]: string } = {
    [ROLE_ADMIN]: 'Admin',
    [ROLE_PROJECT_RADAR]: 'Radar de Propostas',
    [ROLE_COLLABORATOR_VIEWER]: 'Visualizar Colaboradores',
    [ROLE_COLLABORATOR_ADMIN]: 'Administrar Colaboradores',
    [ROLE_COLLABORATOR_EDITOR]: 'Editar Colaboradores (sem salário)',
    [ROLE_SALARY_ADMIN]: 'Administrar Salários',
    [ROLE_SALARY_VIEW]: 'Visualizar Salários',
    [ROLE_CALCULATOR]: 'Calculadora (básico)',
    [ROLE_EVALUATION_ADMIN]: 'Administrar Avaliações',
    [ROLE_VACATION_DP]: 'DP - Gestão',
    [ROLE_VACATION_VIEWER]: 'DP - Dashboard',
    [ROLE_SI_ADMIN]: 'Administrar SI',
    [ROLE_SI_RH]: 'Chamados DP',
    [ROLE_PUBLICADOR]: 'Publicar no Portal',
    [ROLE_RECRUITMENT_ADMIN]: 'Recrutamento',
};

const allRoles = [
    ROLE_ADMIN,
    ROLE_PROJECT_RADAR,
    ROLE_COLLABORATOR_VIEWER,
    ROLE_COLLABORATOR_ADMIN,
    ROLE_COLLABORATOR_EDITOR,
    ROLE_SALARY_ADMIN,
    ROLE_SALARY_VIEW,
    ROLE_CALCULATOR,
    ROLE_EVALUATION_ADMIN,
    ROLE_VACATION_DP,
    ROLE_VACATION_VIEWER,
    ROLE_SI_ADMIN,
    ROLE_SI_RH,
    ROLE_PUBLICADOR,
    ROLE_RECRUITMENT_ADMIN,
];

export default function Users() {
    const { user: loggedInUser, userHasRole } = useAuth();

    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserRoles, setNewUserRoles] = useState<string[]>([]);

    const [isEditRolesModalOpen, setIsEditRolesModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [selectedRolesForEdit, setSelectedRolesForEdit] = useState<string[]>([]);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const canViewAdminPage = useMemo(() => {
        if (!loggedInUser) return false;
        return userHasRole(ROLE_ADMIN);
    }, [loggedInUser, userHasRole]);

    useEffect(() => {
        if (canViewAdminPage) {
            loadUsers();
        } else {
            setLoading(false);
        }
    }, [canViewAdminPage]);

    const loadUsers = async () => {
        setLoading(true);
        try {
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const usersList = usersSnapshot.docs.map(doc => ({
                id: doc.id, // Agora doc.id é o email
                // Usuário criado direto no console do Firebase pode não ter o campo `email` — o id
                // do documento já é o e-mail (sem isso, o e-mail some da lista e editar/excluir quebra).
                email: doc.data().email ?? doc.id,
                displayName: doc.data().displayName,
                photoURL: doc.data().photoURL,
                roles: doc.data().roles || [],
            })) as User[];
            setUsers(usersList);
        } catch (err) {
            console.error('Erro ao carregar usuários:', err);
            setError('Erro ao carregar usuários');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenAddUserModal = () => {
        setNewUserEmail('');
        setNewUserRoles([]);
        setError(null);
        setSuccess(null);
        setIsAddUserModalOpen(true);
    };

    const handleCloseAddUserModal = () => {
        setIsAddUserModalOpen(false);
        setNewUserEmail('');
        setNewUserRoles([]);
    };

    const handleAddUserSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setIsSubmitting(true);

        try {
            const emailKey = newUserEmail.trim();

            // Verificar se usuário já existe usando getDoc ao invés de query
            const userRef = doc(db, 'users', emailKey);
            const existingUser = await getDoc(userRef);

            if (existingUser.exists()) {
                setError('Este email já está cadastrado');
                setIsSubmitting(false);
                return;
            }

            // Criar usuário com email como chave do documento
            await setDoc(userRef, {
                email: emailKey,
                roles: newUserRoles,
                createdAt: new Date()
            });

            setSuccess('Usuário adicionado com sucesso!');
            await loadUsers();
            handleCloseAddUserModal();
        } catch (err) {
            console.error('Erro ao salvar usuário:', err);
            setError('Erro ao salvar usuário');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRoleChangeForNewUser = (role: string) => {
        setNewUserRoles(prev =>
            prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
        );
    };

    const handleOpenEditRolesModal = (userToEdit: User) => {
        setEditingUser(userToEdit);
        setSelectedRolesForEdit([...userToEdit.roles]);
        setError(null);
        setSuccess(null);
        setIsEditRolesModalOpen(true);
    };

    const handleCloseEditRolesModal = () => {
        setIsEditRolesModalOpen(false);
        setEditingUser(null);
        setSelectedRolesForEdit([]);
    };

    const handleEditRolesSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingUser) return;
        setError(null);
        setSuccess(null);
        setIsSubmitting(true);

        try {
            // Usar email como chave do documento ao invés do ID aleatório
            const userRef = doc(db, 'users', editingUser.id);
            await updateDoc(userRef, {
                roles: selectedRolesForEdit,
                // Conserta usuário criado pelo console sem o campo (ver loadUsers).
                email: editingUser.email,
            });
            setSuccess('Funções do usuário atualizadas com sucesso!');
            await loadUsers();
            handleCloseEditRolesModal();
        } catch (err) {
            console.error('Erro ao atualizar funções:', err);
            setError('Erro ao atualizar funções do usuário');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRoleChangeForEdit = (role: string) => {
        setSelectedRolesForEdit(prev =>
            prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
        );
    };

    const handleDeleteUser = async (userEmail: string) => {
        if (window.confirm('Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita.')) {
            setError(null);
            setSuccess(null);
            try {
                // Usar email como chave do documento ao invés do ID aleatório
                await deleteDoc(doc(db, 'users', userEmail));
                setSuccess('Usuário excluído com sucesso!');
                await loadUsers();
            } catch (err) {
                console.error('Erro ao excluir usuário:', err);
                setError('Erro ao excluir usuário');
            }
        }
    };

    if (!loggedInUser) {
        return <div className="text-gray-500">Carregando usuário...</div>;
    }

    if (!canViewAdminPage) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="text-center">
                    <h1 className="text-2xl font-semibold text-gray-700 mb-4">Acesso Negado</h1>
                    <p className="text-gray-500">Você não tem permissão para visualizar esta página.</p>
                    <Link to="/" className="mt-6 inline-block bg-indigo-600 text-white px-6 py-2 rounded hover:bg-indigo-700">
                        Voltar para o Dashboard
                    </Link>
                </div>
            </div>
        );
    }

    if (loading) {
        return <div className="text-center py-10 text-gray-500">Carregando usuários...</div>;
    }

    return (
        <div className="container mx-auto p-4 space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Gerenciar Usuários</h2>
                <button
                    onClick={handleOpenAddUserModal}
                    className="btn-primary flex items-center"
                >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Adicionar Usuário
                </button>
            </div>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <strong className="font-bold">Erro: </strong>
                    <span className="block sm:inline">{error}</span>
                    <span className="absolute top-0 bottom-0 right-0 px-4 py-3" onClick={() => setError(null)}>
                        <XMarkIcon className="h-6 w-6 text-red-500 cursor-pointer" />
                    </span>
                </div>
            )}
            {success && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
                    <strong className="font-bold">Sucesso: </strong>
                    <span className="block sm:inline">{success}</span>
                    <span className="absolute top-0 bottom-0 right-0 px-4 py-3" onClick={() => setSuccess(null)}>
                        <XMarkIcon className="h-6 w-6 text-green-500 cursor-pointer" />
                    </span>
                </div>
            )}

            <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <ul className="divide-y divide-gray-200">
                    {users.length === 0 && (
                        <li className="px-4 py-4 text-center text-gray-500 sm:px-6">Nenhum usuário cadastrado.</li>
                    )}
                    {users.map((userItem) => (
                        <li key={userItem.id}>
                            <div className="px-4 py-4 flex items-center justify-between sm:px-6 hover:bg-gray-50">
                                <div className="flex items-center flex-1 min-w-0">
                                    <div className="flex-shrink-0 mr-3">
                                        {userItem.photoURL ? (
                                            <img className="h-10 w-10 rounded-full" src={userItem.photoURL} alt="" />
                                        ) : (
                                            <UserCircleIcon className="h-10 w-10 text-gray-400" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-indigo-600 truncate">
                                            {userItem.displayName || userItem.email}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {userItem.displayName ? userItem.email : ''}
                                        </p>
                                        <div className="mt-1 flex flex-wrap gap-1">
                                            {userItem.roles.length > 0 ? userItem.roles.map(role => (
                                                <span key={role} className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
                                                    {roleDisplayNames[role] || role}
                                                </span>
                                            )) : <span className="text-xs text-gray-400 italic">Nenhuma função</span>}
                                        </div>
                                    </div>
                                </div>
                                <div className="ml-4 flex-shrink-0 space-x-2">
                                    <button
                                        onClick={() => handleOpenEditRolesModal(userItem)}
                                        className="btn-icon text-indigo-600 hover:text-indigo-900"
                                        title="Editar Funções"
                                    >
                                        <PencilIcon className="h-5 w-5" />
                                    </button>
                                    <button
                                        onClick={() => handleDeleteUser(userItem.email)}
                                        className="btn-icon text-red-600 hover:text-red-900"
                                        title="Excluir Usuário"
                                    >
                                        <TrashIcon className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            {isAddUserModalOpen && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full transform transition-all">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-semibold text-gray-900">Adicionar Novo Usuário</h3>
                            <button onClick={handleCloseAddUserModal} className="text-gray-400 hover:text-gray-600">
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>
                        <form onSubmit={handleAddUserSubmit} className="space-y-4">
                            <div>
                                <label htmlFor="newUserEmail" className="block text-sm font-medium text-gray-700">E-mail do Usuário</label>
                                <input
                                    type="email"
                                    id="newUserEmail"
                                    value={newUserEmail}
                                    onChange={(e) => setNewUserEmail(e.target.value)}
                                    className="input-primary mt-1 w-full"
                                    required
                                    placeholder="usuario@exemplo.com"
                                    disabled={isSubmitting}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Funções Iniciais</label>
                                <div className="space-y-2">
                                    {allRoles.map(role => (
                                        <label key={role} className="flex items-center space-x-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                className="form-checkbox h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                                checked={newUserRoles.includes(role)}
                                                onChange={() => handleRoleChangeForNewUser(role)}
                                                disabled={isSubmitting}
                                            />
                                            <span className="text-sm text-gray-700">{roleDisplayNames[role] || role}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            {error && <p className="text-sm text-red-600">{error}</p>}
                            <div className="flex justify-end space-x-3 pt-3">
                                <button type="button" onClick={handleCloseAddUserModal} className="btn-secondary" disabled={isSubmitting}>Cancelar</button>
                                <button type="submit" className="btn-primary" disabled={isSubmitting}>
                                    {isSubmitting ? 'Adicionando...' : 'Adicionar Usuário'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isEditRolesModalOpen && editingUser && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full transform transition-all">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-semibold text-gray-900">Editar Funções de {editingUser.displayName || editingUser.email}</h3>
                            <button onClick={handleCloseEditRolesModal} className="text-gray-400 hover:text-gray-600">
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>
                        <form onSubmit={handleEditRolesSubmit} className="space-y-4">
                            <div>
                                <p className="text-sm text-gray-500">Email: <span className="font-medium text-gray-700">{editingUser.email}</span></p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Funções</label>
                                <div className="space-y-2">
                                    {allRoles.map(role => (
                                        <label key={role} className="flex items-center space-x-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                className="form-checkbox h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                                checked={selectedRolesForEdit.includes(role)}
                                                onChange={() => handleRoleChangeForEdit(role)}
                                                disabled={isSubmitting}
                                            />
                                            <span className="text-sm text-gray-700">{roleDisplayNames[role] || role}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            {error && <p className="text-sm text-red-600">{error}</p>}
                            <div className="flex justify-end space-x-3 pt-3">
                                <button type="button" onClick={handleCloseEditRolesModal} className="btn-secondary" disabled={isSubmitting}>Cancelar</button>
                                <button type="submit" className="btn-primary" disabled={isSubmitting}>
                                    {isSubmitting ? 'Salvando...' : 'Salvar Funções'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
} 
